/**
 * Eine Tageszelle im Raster (`kal-tag-<datum>`, Spec 07 „Das Raster") — Kopf
 * (Wochentag, Tageszahl, bei `is_today` das Wort „heute"), darunter höchstens
 * drei Einträge, danach `kal-tag-mehr-<datum>` (AK-465). **Dieselbe
 * Schaltfläche** klappt auf und wieder zu — kein zweites Element für „zu":
 * verschwände sie beim Aufklappen, hätte ein zweiter Klick nichts mehr zum
 * Zutreffen.
 *
 * 92px Mindesthöhe ist gemessen, nicht gewählt (Spec: „Kopf 17px + 3 × 22px +
 * 2 × 4px Abstand = 91px").
 *
 * `kal-tag-add-<datum>` (Spec 45: Auslöser des Anlege-Menüs `CalendarAddMenu`,
 * Termin · Aufgabe) erscheint per `group-hover`/`group-focus-within` —
 * auf Touch gibt es kein Hover, und `lib/viewport.ts` schaltet das Raster ab
 * 768px unabhängig vom Zeigertyp, ein Tablet stünde also vor einem
 * unsichtbaren 24px-Knopf (W-D2, `docs/work/007-kalender-af4593/
 * findings.md`, Tor 2). Die Spec sieht dafür einen zweiten Weg vor: „auf
 * Touch löst ein Tipp auf die leere Fläche der Zelle dasselbe aus" — über
 * `onPointerUp` mit `pointerType === 'touch'`, nicht `onClick`, damit ein
 * Maus-Klick zwischen den Einträgen (keine sichtbare Aufforderung dort)
 * weiterhin nichts auslöst. Der Selektor schließt einen Eintrag (Link,
 * `kal-eintrag-<id>` — auch das nicht klickbare `type: 'none'`-`<div>` aus
 * `CalendarEntryRow.tsx`) und `kal-tag-mehr-<datum>` aus, die haben ihr
 * eigenes Ziel.
 */

import type { PointerEvent } from 'react'

import { CalendarAddMenu } from '@/features/calendar/CalendarAddMenu'
import { CalendarEntryRow } from '@/features/calendar/CalendarEntryRow'
import type { CalendarEntryActions } from '@/features/calendar/CalendarEntryRow'
import type { CalendarDay } from '@/features/calendar/api'

const VISIBLE_LIMIT = 3

interface CalendarDayCellProps {
  readonly day: CalendarDay
  readonly isExpanded: boolean
  readonly actions: CalendarEntryActions
  readonly onToggleExpand: () => void
  readonly onAddAppointment: () => void
  readonly onAddTask: () => void
}

export function CalendarDayCell({
  day,
  isExpanded,
  actions,
  onToggleExpand,
  onAddAppointment,
  onAddTask,
}: CalendarDayCellProps) {
  const visibleEntries = isExpanded ? day.entries : day.entries.slice(0, VISIBLE_LIMIT)
  const hasMore = day.total > VISIBLE_LIMIT

  // Ein Tipp auf die leere Fläche öffnet dasselbe Menü wie das Plus (Spec 45,
  // R6): Der Auslöser selbst wird ausgelöst, damit es nur einen Weg gibt.
  function handlePointerUp(event: PointerEvent<HTMLDivElement>) {
    if (event.pointerType !== 'touch') return
    if (event.target instanceof HTMLElement && event.target.closest('a, button, [data-testid^="kal-eintrag-"]') !== null) {
      return
    }
    event.currentTarget.querySelector<HTMLButtonElement>(`[data-testid="kal-tag-add-${day.on}"]`)?.click()
  }

  return (
    <div
      data-testid={`kal-tag-${day.on}`}
      onPointerUp={handlePointerUp}
      className={`group border-border rounded-card relative flex min-h-[92px] flex-col gap-1 border p-1.5 ${
        day.is_weekend ? 'bg-bg' : 'bg-surface'
      } ${day.is_today ? 'border-accent border-2' : ''}`}
    >
      <div className="flex items-start justify-between gap-1">
        <span className="flex items-baseline gap-1">
          <span className={`text-label uppercase ${day.is_today ? 'text-accent' : 'text-muted'}`}>
            {day.weekday_display}
          </span>
          <span data-numeric className={`text-body font-semibold ${day.is_today ? 'text-accent' : 'text-text'}`}>
            {day.day_display}
          </span>
        </span>
        {day.is_today && (
          <span data-testid="kal-tag-heute" className="text-label text-accent">
            heute
          </span>
        )}
      </div>

      <CalendarAddMenu
        variant="cell"
        triggerTestId={`kal-tag-add-${day.on}`}
        menuTestId={`kal-tag-add-menu-${day.on}`}
        triggerLabel={`Am ${day.day_display}. anlegen`}
        onAddAppointment={onAddAppointment}
        onAddTask={onAddTask}
      />

      <div className="flex flex-col gap-1">
        {visibleEntries.map((entry) => (
          <CalendarEntryRow key={entry.id} entry={entry} actions={actions} size="compact" />
        ))}
      </div>

      {hasMore && (
        <button
          type="button"
          data-testid={`kal-tag-mehr-${day.on}`}
          aria-expanded={isExpanded}
          onClick={onToggleExpand}
          className="text-label text-accent text-left hover:underline"
        >
          {isExpanded ? 'Weniger anzeigen' : `+${String(day.total - VISIBLE_LIMIT)} weitere`}
        </button>
      )}
    </div>
  )
}
